import type { Vec2 } from './types.js';
import { TORTUGA_POS } from './constants.js';

export type PortRegion = 'europe' | 'caribbean';

export interface Port {
  id: string;
  name: string;
  country: string;
  region: PortRegion;
  flavor: string;
  /** One-line summary of the voyage from this port. */
  difficulty: string;
  /** Center of the offshore embark area — the captain spawns near here, with jitter.
   *  Also the arrival target: a leg is complete within PORT_ARRIVAL_RADIUS_KM of this point. */
  position: Vec2;
  /** Where the city itself sits, on the coast. Drawn as a city marker on the chart. */
  cityPosition: Vec2;
  /** Direction the ship faces leaving harbor (radians, 0=E π/2=S π=W 3π/2=N). */
  outboundHeading: number;
}

/**
 * Ports of call. The five European ports (plus Genoa) are the ports of embarkation;
 * Tortuga is the destination of every first voyage. Once a captain has made a crossing
 * they may shove off again and sail back — bouncing between Europe and the Indies for
 * as long as their stores and patience hold.
 *
 * Coordinates are kilometers from Cádiz (X+ = East, Y+ = South), roughly true to scale.
 */
export const PORTS: Port[] = [
  {
    id: 'lisbon',
    name: 'Lisbon',
    country: 'Portugal',
    region: 'europe',
    flavor: 'Mouth of the Tagus, where the old Portuguese maps were drawn. A favorable wind to the south and west.',
    difficulty: 'The shortest crossing. Catch the trades and run.',
    cityPosition: { x: -75, y: -255 },
    position: { x: -260, y: -240 },
    outboundHeading: Math.PI + 0.2,
  },
  {
    id: 'cadiz',
    name: 'Cádiz',
    country: 'Spain',
    region: 'europe',
    flavor: 'The southern Spanish gateway to the New World. A straight shot at the trade winds and the Indies.',
    difficulty: 'A southern run on the trade winds.',
    cityPosition: { x: 5, y: -25 },
    position: { x: 0, y: 30 },
    outboundHeading: Math.PI,
  },
  {
    id: 'brest',
    name: 'Brest',
    country: 'France',
    region: 'europe',
    flavor: 'On the rocky tip of Brittany. The Bay of Biscay must be crossed before any voyage can begin.',
    difficulty: 'A Bay of Biscay crossing comes first.',
    cityPosition: { x: -355, y: -1150 },
    position: { x: -440, y: -1170 },
    outboundHeading: Math.PI + 0.4,
  },
  {
    id: 'plymouth',
    name: 'Plymouth',
    country: 'England',
    region: 'europe',
    flavor: 'A privateer\'s harbor at the western tip of England. From here, the Channel opens west into the broad Atlantic.',
    difficulty: 'Clear the Channel, then it\'s open ocean.',
    cityPosition: { x: -370, y: -1505 },
    position: { x: -440, y: -1470 },
    outboundHeading: Math.PI + 0.3,
  },
  {
    id: 'genoa',
    name: 'Genoa',
    country: 'Republic of Genoa',
    region: 'europe',
    flavor: 'The busiest counting-house in the Mediterranean. Your ships have traded here since before Columbus. Now you sail west.',
    difficulty: 'The longest road. Thread the Tyrrhenian Sea, pass Gibraltar, then face the Atlantic.',
    cityPosition: { x: 1290, y: -880 },
    position: { x: 1290, y: -830 },
    outboundHeading: Math.PI + 0.5,  // WSW, toward Gibraltar
  },
  {
    id: 'copenhagen',
    name: 'Copenhagen',
    country: 'Denmark',
    region: 'europe',
    flavor: 'A long road. You must thread the Skagerrak, round Scotland or sail the Channel, then cross the Atlantic. Months added to your voyage.',
    difficulty: 'A months-long ordeal. The North Sea must be crossed first.',
    cityPosition: { x: 770, y: -2390 },
    position: { x: 770, y: -2440 },
    outboundHeading: Math.PI / 2 + 0.4,
  },
  {
    id: 'tortuga',
    name: 'Tortuga',
    country: 'The Spanish Main',
    region: 'caribbean',
    flavor: 'A pirate haven off the north coast of Hispaniola. Rum, fish, and possibility. Every crossing ends here — and from here, every crossing home begins.',
    difficulty: 'The long road back. Ride the Gulf Stream north, then the westerlies east.',
    cityPosition: { x: TORTUGA_POS.x, y: TORTUGA_POS.y },
    // Anchorage ~35 km north of the town, in open water clear of the Hispaniola coast.
    position: { x: TORTUGA_POS.x, y: TORTUGA_POS.y - 35 },
    outboundHeading: 2 * Math.PI - 0.35,  // ENE, out toward the open Atlantic
  },
];

export function getPort(id: string): Port | undefined {
  return PORTS.find(p => p.id === id);
}

/** Ports a brand-new captain may embark from (the European ports). */
export const EMBARK_PORTS: Port[] = PORTS.filter(p => p.region === 'europe');

/**
 * Where a captain may sail next from a given port. From Tortuga: any European
 * port. From a European port: Tortuga only. Unknown port → no destinations.
 */
export function destinationsFrom(portId: string | null): Port[] {
  const here = portId ? getPort(portId) : undefined;
  if (!here) return [];
  return here.region === 'caribbean'
    ? PORTS.filter(p => p.region === 'europe')
    : PORTS.filter(p => p.region === 'caribbean');
}
